import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type {
  Company,
  CreateCompany,
  UpdateCompany,
  CompanyQuery,
  Contact,
  CreateContact,
  UpdateContact,
  PaginatedResponse,
} from '@estimator/shared';

// ─── Company with contacts (detail response) ────────────────────
interface CompanyWithContacts extends Company {
  contacts: Contact[];
  project_count: number;
}

// ─── Companies ───────────────────────────────────────────────────

export function useCompanies(params?: Partial<CompanyQuery>) {
  return useQuery({
    queryKey: ['companies', params],
    queryFn: async () => {
      const { data } = await apiClient.get<PaginatedResponse<Company>>(
        '/companies',
        { params },
      );
      return data;
    },
  });
}

export function useCompany(id: string | null) {
  return useQuery({
    queryKey: ['company', id],
    queryFn: async () => {
      const { data } = await apiClient.get<CompanyWithContacts>(
        `/companies/${id}`,
      );
      return data;
    },
    enabled: !!id,
  });
}

export function useCreateCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateCompany) => {
      const { data } = await apiClient.post<Company>('/companies', body);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['companies'] }),
  });
}

export function useUpdateCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateCompany & { id: string }) => {
      const { data } = await apiClient.patch<Company>(`/companies/${id}`, body);
      return data;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      queryClient.invalidateQueries({ queryKey: ['company', vars.id] });
    },
  });
}

export function useDeleteCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/companies/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['companies'] }),
  });
}

// ─── Contacts ────────────────────────────────────────────────────

export function useCreateContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ companyId, ...body }: CreateContact & { companyId: string }) => {
      const { data } = await apiClient.post<Contact>(
        `/companies/${companyId}/contacts`,
        body,
      );
      return data;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['company', vars.companyId] });
    },
  });
}

export function useUpdateContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      companyId,
      contactId,
      ...body
    }: UpdateContact & { companyId: string; contactId: string }) => {
      const { data } = await apiClient.patch<Contact>(
        `/companies/${companyId}/contacts/${contactId}`,
        body,
      );
      return data;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['company', vars.companyId] });
    },
  });
}

export function useDeleteContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ companyId, contactId }: { companyId: string; contactId: string }) => {
      await apiClient.delete(`/companies/${companyId}/contacts/${contactId}`);
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['company', vars.companyId] });
    },
  });
}
